import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { api } from "../lib/api";
import { DEFAULT_PRIVACY_POLICY } from "../content/privacyPolicy";

/** Public privacy policy — default content, overridable via studio privacy-settings API. */
export default function PrivacyNoticePage() {
  const [notice, setNotice] = useState(DEFAULT_PRIVACY_POLICY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/public/privacy-notice");
        if (cancelled) return;
        const body = (data.privacy_notice || "").trim();
        if (body) setNotice(body);
      } catch {
        /* keep default policy */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[var(--t-bg)] px-5 py-10 md:px-12 md:py-16" data-testid="privacy-notice-page">
      <div className="mx-auto max-w-2xl">
        <div className="label-eyebrow mb-3">TAĪSTU</div>
        {loading ? (
          <p className="t-meta">Loading…</p>
        ) : (
          <div
            className="privacy-policy text-sm leading-relaxed text-[var(--t-text)]"
            data-testid="privacy-notice-body"
          >
            <ReactMarkdown
              remarkPlugins={[remarkBreaks]}
              components={{
                h1: ({ children }) => (
                  <h1 className="font-heading mb-2 text-3xl tracking-tight !font-extralight md:text-4xl">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="font-heading mt-10 mb-3 text-xl tracking-tight !font-light first:mt-0">
                    {children}
                  </h2>
                ),
                p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
                strong: ({ children }) => (
                  <strong className="font-medium text-[var(--t-text)]">{children}</strong>
                ),
                ul: ({ children }) => (
                  <ul className="mb-4 list-disc space-y-2 pl-5 last:mb-0">{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol className="mb-4 list-decimal space-y-2 pl-5 last:mb-0">{children}</ol>
                ),
                li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                hr: () => <hr className="my-8 border-0 border-t border-[var(--t-border)]" />,
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="text-[var(--t-accent)] underline-offset-2 hover:underline"
                  >
                    {children}
                  </a>
                ),
              }}
            >
              {notice}
            </ReactMarkdown>
          </div>
        )}

        <p className="mt-12">
          <Link to="/" className="label-eyebrow text-[var(--t-meta)] hover:text-[var(--t-accent)]">
            ← Back
          </Link>
        </p>
      </div>
    </div>
  );
}
